// MaaPrint i18n — English / Telugu / Hindi translations.
// Add new keys under `en` first, then translate.

export const LANGUAGES = [
  { code: "en", label: "English", native: "English" },
  { code: "te", label: "Telugu", native: "తెలుగు" },
  { code: "hi", label: "Hindi", native: "हिन्दी" },
];

const en = {
  brand: { tagline: "Smart Cloud Printing Platform", name: "MaaPrint" },
  common: {
    signIn: "Sign in", signOut: "Sign out", register: "Register",
    email: "Email", password: "Password", name: "Full name", phone: "Mobile",
    save: "Save", cancel: "Cancel", delete: "Delete", edit: "Edit",
    submit: "Submit", loading: "Loading…", search: "Search",
    yes: "Yes", no: "No", ok: "OK", back: "Back", close: "Close",
    online: "Online", offline: "Offline", pending: "Pending",
    printing: "Printing", completed: "Completed", cancelled: "Cancelled",
    failed: "Failed", queued: "Queued",
    all: "All", none: "None", copies: "Copies", pages: "Pages",
    color: "Color", bw: "Black & White", duplex: "Duplex",
    paper: "Paper", orientation: "Orientation", portrait: "Portrait",
    landscape: "Landscape", download: "Download", upload: "Upload",
    filename: "File", status: "Status", amount: "Amount", date: "Date",
    actions: "Actions", type: "Type", welcome: "Welcome back",
    language: "Language",
  },
  nav: {
    dashboard: "Dashboard", newPrint: "New Print", bulkPrint: "Bulk Print",
    imagePrint: "Image Print", passport: "Passport Photo", idCard: "ID Card",
    orders: "Orders", wallet: "Wallet", printers: "Printers",
    connector: "Connector", reports: "Reports", users: "Users",
    settings: "Settings", subscription: "Subscription", finance: "Finance",
    myShop: "My Shop (QR)", printStudio: "Print Studio",
    help: "Help & Support", about: "About MaaPrint",
  },
  auth: {
    signInTitle: "Sign in to MaaPrint",
    orRegister: "Or create a new account",
    forgot: "Forgot?",
    tryDemo: "Try a demo account",
    createTitle: "Create your account",
    haveAccount: "Already have one? Sign in",
    iAmA: "I am a…",
    role_customer: "Customer",
    role_retailer: "Retailer / CSC Center",
    role_distributor: "Distributor",
    role_super_admin: "Super Admin",
    minPwd: "Password (min 6 characters)",
    resetTitle: "Reset your password",
    resetSubtitle: "Enter your email and we’ll send you a reset link.",
    resetSent: "If that email is registered, you’ll receive a reset link shortly.",
    sendLink: "Send reset link",
    backToSignIn: "Back to sign in",
  },
  dashboard: {
    overview: "Overview",
    todaysPrints: "Today's Prints",
    totalPages: "Total Pages",
    revenue: "Revenue",
    platformRevenue: "Platform Revenue",
    totalSpend: "Total Spend",
    walletBalance: "Wallet Balance",
    recharge: "Recharge →",
    lastSevenDays: "Last 7 days",
    printActivity: "Print activity",
    trending: "Trending",
    printerStatus: "Printer status",
    onlinePrinters: "Online printers",
    readyToPrint: "Ready to print",
    totalPrinters: "Total printers",
    allConfigured: "All configured",
    managePrinters: "Manage printers",
    recentActivity: "Recent activity",
    recentOrders: "Recent orders",
    seeAll: "See all →",
    noPrints: "No prints yet.",
    startPrinting: "Start printing →",
    newPrintBtn: "New print",
    allOrders: "All orders",
    pendingJobs: "Pending jobs",
  },
  print: {
    pageTitle: "New print job",
    subtitle: "Upload a PDF, choose settings, and MaaPrint will silently print it to your default printer.",
    dropzone: "Drop PDF here or click to upload",
    dropzoneHint: "Max 15 MB · PDF only",
    settings: "Print settings",
    summary: "Auto-print summary",
    mode: "Mode", modeColor: "Color", modeBW: "Black & White",
    duplexOn: "Both sides", duplexOff: "One side",
    yourWallet: "Your wallet",
    autoPrintNow: "Auto-print now",
    sending: "Sending…",
    silentNote: "Silent background print · Wallet auto-deducted",
    ready: "Print completed!",
    readyNote: "Silent auto-print delivered to",
    printAnother: "Print another",
    viewOrders: "View all orders",
    charged: "Charged",
    perPageFrom: "Est. per page (from)",
    gst: "GST",
  },
  wallet: {
    title: "Wallet", currentBalance: "Current balance",
    autoDeduct: "Auto-deducted on every print job",
    addMoney: "Add money", history: "Transaction history",
    empty: "No transactions yet.", credit: "Credit", debit: "Debit",
    recharge: "Recharge wallet", processing: "Processing…", now: "Recharge now",
    razorpayHint: "Real Razorpay payment when configured. Currently in mock mode.",
    entries: "entries", description: "Description",
  },
  connector: {
    title: "Windows Connector",
    subtitle: "Install the MaaPrint agent on your Windows PC to enable silent auto-print.",
    generate: "Generate connector key",
    regenerate: "Regenerate key",
    downloadPackage: "Download connector (.zip)",
    setupSteps: "Setup steps",
    yourKey: "Your connector key",
    keyWarn: "This key is shown only once — save it now.",
    step1: "Install Python 3.9+ and SumatraPDF on your Windows PC.",
    step2: "Download the connector ZIP below and extract it.",
    step3: "Copy config.example.json to config.json and paste your key.",
    step4: "Run `python maaprint_connector.py --config config.json`.",
    step5: "Optional: add to Windows startup for auto-start on boot.",
    connected: "Connected agents",
    lastSeen: "Last seen",
    noConnectors: "No connectors registered yet.",
  },
  printers: {
    title: "Printers",
    subtitle: "Auto-detected via the Windows connector. Add manual printers below.",
    addPrinter: "Add printer",
    name: "Name", brand: "Brand", model: "Model",
    setDefault: "Set as default", default: "Default",
    testPrint: "Test", empty: "No printers connected yet",
    emptyHint: "Add your first printer to enable auto-print.",
  },
  orders: {
    title: "Orders", history: "Print history",
    filenameCol: "File", user: "User",
    pagesCopies: "Pages × Copies",
    mode: "Mode", printer: "Printer", cost: "Cost",
    reprint: "Reprint", cancel: "Cancel", empty: "No orders found.",
    filterAll: "All statuses", exportCsv: "Export CSV",
  },
  bulk: {
    title: "Bulk PDF Print",
    subtitle: "Drop many PDFs at once — each will queue as its own print job.",
    dropzone: "Drop multiple PDFs here or click to browse",
    hint: "Up to 20 files, 15 MB each",
    printAll: "Print all",
    printAllQueue: "Send {count} print jobs",
    filesReady: "Files ready to print",
  },
  image: {
    title: "Image Print",
    subtitle: "Print JPG, PNG or WebP images — auto-scaled to paper size.",
    dropzone: "Drop image here or click to browse",
    hint: "Max 10 MB · JPG, PNG, WebP",
    fit: "Fit", fitContain: "Fit inside page", fitCover: "Fill page",
    crop: "Crop & adjust",
    apply: "Apply crop", printNow: "Print now",
  },
  passport: {
    title: "Passport Photo Editor",
    subtitle: "Auto-composed A4 sheet with cut lines. Choose size, upload portrait, print.",
    size: "Photo size",
    copies: "Copies per sheet",
    generate: "Print sheet",
    tips: "Tip: use a clear front-facing photo with plain background.",
  },
  idCard: {
    title: "ID Card Designer",
    subtitle: "Create ISO-sized (85.6 × 54 mm) ID cards. Fill fields, upload photo, print.",
    template: "Template",
    tpl_employee: "Employee", tpl_student: "Student", tpl_shop: "Shop pass",
    fields: { name: "Full name", id_no: "ID number", role: "Role",
              org: "Organisation", phone: "Phone" },
    generate: "Print ID card",
  },
  reports: {
    title: "Reports", analytics: "Analytics",
    daily: "Daily", weekly: "Weekly", monthly: "Monthly",
    orders: "Orders", pages: "Pages", revenue: "Revenue",
    colorBw: "Color / BW", trends: "Trends", ordersRevenue: "Orders & Revenue",
    noData: "No data for this period yet.",
    daysCount: "days",
  },
  users: {
    title: "Users",
    role: "Role", wallet: "Wallet", statusCol: "Status",
    active: "Active", inactive: "Inactive",
  },
  settings: {
    title: "Platform settings",
    subtitle: "Pricing, taxes and commission rates.",
    priceBw: "Per page B&W (₹)", priceColor: "Per page Color (₹)",
    duplexExtra: "Duplex extra per page (₹)", gstPct: "GST %",
    retailerPct: "Retailer commission %", distributorPct: "Distributor commission %",
    notifyEmail: "Send email notifications",
    saveChanges: "Save changes",
  },
};

// Telugu — human-verified basics for retailer-facing UX
const te = {
  brand: { tagline: "స్మార్ట్ క్లౌడ్ ప్రింటింగ్ ప్లాట్‌ఫారమ్", name: "మా‌ప్రింట్" },
  common: {
    signIn: "సైన్ ఇన్", signOut: "సైన్ అవుట్", register: "నమోదు",
    email: "ఇమెయిల్", password: "పాస్‌వర్డ్", name: "పూర్తి పేరు", phone: "మొబైల్",
    save: "సేవ్", cancel: "రద్దు", delete: "తొలగించు", edit: "సవరించు",
    submit: "సమర్పించు", loading: "లోడ్ అవుతోంది…", search: "వెతకండి",
    yes: "అవును", no: "కాదు", ok: "సరే", back: "వెనుకకు", close: "మూసివేయి",
    online: "ఆన్‌లైన్", offline: "ఆఫ్‌లైన్", pending: "పెండింగ్",
    printing: "ప్రింటింగ్", completed: "పూర్తయింది", cancelled: "రద్దు",
    failed: "విఫలం", queued: "క్యూలో",
    all: "అన్నీ", none: "ఏదీ లేదు", copies: "కాపీలు", pages: "పేజీలు",
    color: "రంగు", bw: "నలుపు తెలుపు", duplex: "డ్యూప్లెక్స్",
    paper: "కాగితం", orientation: "దిశ", portrait: "నిలువు",
    landscape: "అడ్డం", download: "డౌన్‌లోడ్", upload: "అప్‌లోడ్",
    filename: "ఫైల్", status: "స్థితి", amount: "మొత్తం", date: "తేదీ",
    actions: "చర్యలు", type: "రకం", welcome: "తిరిగి స్వాగతం",
    language: "భాష",
  },
  nav: {
    dashboard: "డాష్‌బోర్డ్", newPrint: "కొత్త ప్రింట్", bulkPrint: "బల్క్ ప్రింట్",
    imagePrint: "ఇమేజ్ ప్రింట్", passport: "పాస్‌పోర్ట్ ఫోటో", idCard: "ఐడి కార్డ్",
    orders: "ఆర్డర్లు", wallet: "వాలెట్", printers: "ప్రింటర్లు",
    connector: "కనెక్టర్", reports: "నివేదికలు", users: "వినియోగదారులు",
    settings: "సెట్టింగ్‌లు", subscription: "సబ్‌స్క్రిప్షన్", finance: "ఫైనాన్స్",
    myShop: "నా షాప్ (QR)", printStudio: "ప్రింట్ స్టూడియో",
    help: "సహాయం & మద్దతు", about: "MaaPrint గురించి",
  },
  auth: {
    signInTitle: "మా‌ప్రింట్‌కి సైన్ ఇన్ చేయండి",
    orRegister: "లేదా కొత్త ఖాతా సృష్టించండి",
    forgot: "మర్చిపోయారా?",
    tryDemo: "డెమో ఖాతా వాడండి",
    createTitle: "మీ ఖాతా సృష్టించండి",
    haveAccount: "ఇప్పటికే ఖాతా ఉందా? సైన్ ఇన్",
    iAmA: "నేను…",
    role_customer: "కస్టమర్",
    role_retailer: "రిటైలర్ / CSC సెంటర్",
    role_distributor: "డిస్ట్రిబ్యూటర్",
    role_super_admin: "సూపర్ అడ్మిన్",
    minPwd: "పాస్‌వర్డ్ (కనీసం 6 అక్షరాలు)",
    resetTitle: "మీ పాస్‌వర్డ్ రీసెట్",
    resetSubtitle: "మీ ఇమెయిల్ నమోదు చేయండి, రీసెట్ లింక్ పంపబడుతుంది.",
    resetSent: "ఇమెయిల్ నమోదైతే, రీసెట్ లింక్ పంపబడింది.",
    sendLink: "రీసెట్ లింక్ పంపు",
    backToSignIn: "సైన్ ఇన్‌కి తిరిగి",
  },
  dashboard: {
    overview: "సమీక్ష",
    todaysPrints: "నేటి ప్రింట్లు",
    totalPages: "మొత్తం పేజీలు",
    revenue: "ఆదాయం",
    platformRevenue: "ప్లాట్‌ఫారమ్ ఆదాయం",
    totalSpend: "మొత్తం ఖర్చు",
    walletBalance: "వాలెట్ బ్యాలెన్స్",
    recharge: "రీచార్జ్ →",
    lastSevenDays: "గత 7 రోజులు",
    printActivity: "ప్రింట్ కార్యకలాపం",
    trending: "ట్రెండింగ్",
    printerStatus: "ప్రింటర్ స్థితి",
    onlinePrinters: "ఆన్‌లైన్ ప్రింటర్లు",
    readyToPrint: "ప్రింట్ చేయడానికి సిద్ధం",
    totalPrinters: "మొత్తం ప్రింటర్లు",
    allConfigured: "అన్ని కాన్ఫిగర్ చేయబడ్డాయి",
    managePrinters: "ప్రింటర్లను నిర్వహించండి",
    recentActivity: "ఇటీవలి కార్యకలాపం",
    recentOrders: "ఇటీవలి ఆర్డర్లు",
    seeAll: "అన్నీ చూడండి →",
    noPrints: "ప్రింట్‌లు ఇంకా లేవు.",
    startPrinting: "ప్రింట్ ప్రారంభించండి →",
    newPrintBtn: "కొత్త ప్రింట్",
    allOrders: "అన్ని ఆర్డర్లు",
    pendingJobs: "పెండింగ్ ఉద్యోగాలు",
  },
  print: {
    pageTitle: "కొత్త ప్రింట్ ఆర్డర్",
    subtitle: "PDFని అప్‌లోడ్ చేయండి, సెట్టింగ్‌లు ఎంచుకోండి — మా‌ప్రింట్ నిశ్శబ్దంగా ప్రింట్ చేస్తుంది.",
    dropzone: "PDFని ఇక్కడ డ్రాప్ చేయండి లేదా క్లిక్ చేయండి",
    dropzoneHint: "గరిష్ఠం 15 MB · PDF మాత్రమే",
    settings: "ప్రింట్ సెట్టింగ్‌లు",
    summary: "ఆటో-ప్రింట్ సారాంశం",
    mode: "మోడ్", modeColor: "రంగు", modeBW: "నలుపు తెలుపు",
    duplexOn: "రెండు వైపులా", duplexOff: "ఒక వైపు",
    yourWallet: "మీ వాలెట్",
    autoPrintNow: "ఆటో-ప్రింట్ చేయండి",
    sending: "పంపబడుతోంది…",
    silentNote: "నిశ్శబ్ద బ్యాక్‌గ్రౌండ్ ప్రింట్ · వాలెట్ ఆటో-తగ్గింపు",
    ready: "ప్రింట్ పూర్తయింది!",
    readyNote: "నిశ్శబ్ద ఆటో-ప్రింట్ అందించబడింది —",
    printAnother: "మరొకటి ప్రింట్",
    viewOrders: "అన్ని ఆర్డర్లు చూడండి",
    charged: "వసూలు చేయబడింది",
    perPageFrom: "పేజీకి అంచనా (నుండి)",
    gst: "GST",
  },
  wallet: {
    title: "వాలెట్", currentBalance: "ప్రస్తుత బ్యాలెన్స్",
    autoDeduct: "ప్రతి ప్రింట్ ఆర్డర్‌కు ఆటో-తగ్గింపు",
    addMoney: "డబ్బు జోడించండి", history: "లావాదేవీ చరిత్ర",
    empty: "లావాదేవీలు లేవు.", credit: "క్రెడిట్", debit: "డెబిట్",
    recharge: "వాలెట్ రీచార్జ్", processing: "ప్రాసెస్ చేస్తోంది…", now: "ఇప్పుడు రీచార్జ్",
    razorpayHint: "కాన్ఫిగర్ చేసినప్పుడు Razorpay అసలైన చెల్లింపు. ప్రస్తుతం మాక్ మోడ్‌లో.",
    entries: "ఎంట్రీలు", description: "వివరణ",
  },
  connector: {
    title: "విండోస్ కనెక్టర్",
    subtitle: "నిశ్శబ్ద ఆటో-ప్రింట్ కోసం మీ Windows PCలో MaaPrint ఏజెంట్ ఇన్‌స్టాల్ చేయండి.",
    generate: "కనెక్టర్ కీ జనరేట్ చేయండి",
    regenerate: "కీని రీజనరేట్ చేయండి",
    downloadPackage: "కనెక్టర్ డౌన్‌లోడ్ (.zip)",
    setupSteps: "సెటప్ దశలు",
    yourKey: "మీ కనెక్టర్ కీ",
    keyWarn: "ఈ కీ ఒకసారి మాత్రమే చూపబడుతుంది — ఇప్పుడే సేవ్ చేయండి.",
    step1: "మీ Windows PCలో Python 3.9+ మరియు SumatraPDF ఇన్‌స్టాల్ చేయండి.",
    step2: "క్రింద ఉన్న ZIP డౌన్‌లోడ్ చేసి ఎక్స్‌ట్రాక్ట్ చేయండి.",
    step3: "config.example.jsonని config.jsonకు కాపీ చేసి కీని పేస్ట్ చేయండి.",
    step4: "`python maaprint_connector.py --config config.json` నడపండి.",
    step5: "ఐచ్ఛికం: ఆటో-స్టార్ట్ కోసం Windows స్టార్టప్‌కి జోడించండి.",
    connected: "కనెక్ట్ చేయబడిన ఏజెంట్లు",
    lastSeen: "చివరిసారి చూసినది",
    noConnectors: "ఇంకా కనెక్టర్‌లు నమోదు కాలేదు.",
  },
  printers: {
    title: "ప్రింటర్లు",
    subtitle: "Windows కనెక్టర్ ద్వారా ఆటో-గుర్తించబడతాయి. మాన్యువల్ ప్రింటర్‌లను క్రింద జోడించండి.",
    addPrinter: "ప్రింటర్ జోడించండి",
    name: "పేరు", brand: "బ్రాండ్", model: "మోడల్",
    setDefault: "డిఫాల్ట్‌గా సెట్", default: "డిఫాల్ట్",
    testPrint: "టెస్ట్", empty: "ఇంకా ప్రింటర్‌లు కనెక్ట్ కాలేదు",
    emptyHint: "ఆటో-ప్రింట్ ప్రారంభించడానికి మీ మొదటి ప్రింటర్‌ని జోడించండి.",
  },
  orders: {
    title: "ఆర్డర్లు", history: "ప్రింట్ చరిత్ర",
    filenameCol: "ఫైల్", user: "వినియోగదారు",
    pagesCopies: "పేజీలు × కాపీలు",
    mode: "మోడ్", printer: "ప్రింటర్", cost: "ఖర్చు",
    reprint: "మళ్ళీ ప్రింట్", cancel: "రద్దు", empty: "ఆర్డర్లు కనుగొనబడలేదు.",
    filterAll: "అన్ని స్థితులు", exportCsv: "CSV ఎగుమతి",
  },
  bulk: {
    title: "బల్క్ PDF ప్రింట్",
    subtitle: "ఒకేసారి అనేక PDFలు — ప్రతి ఒక్కటి ప్రత్యేక ప్రింట్ ఆర్డర్‌గా క్యూ అవుతుంది.",
    dropzone: "అనేక PDFలను ఇక్కడ డ్రాప్ చేయండి లేదా బ్రౌజ్ చేయండి",
    hint: "గరిష్ఠంగా 20 ఫైల్‌లు, ప్రతి 15 MB",
    printAll: "అన్నీ ప్రింట్",
    printAllQueue: "{count} ప్రింట్ ఆర్డర్లను పంపండి",
    filesReady: "ప్రింట్‌కు సిద్ధంగా ఉన్న ఫైల్‌లు",
  },
  image: {
    title: "ఇమేజ్ ప్రింట్",
    subtitle: "JPG, PNG లేదా WebP ఇమేజ్‌లను ప్రింట్ చేయండి — పేపర్ సైజ్‌కు స్వయంచాలక పరిమాణం.",
    dropzone: "ఇమేజ్‌ను ఇక్కడ డ్రాప్ చేయండి లేదా బ్రౌజ్ చేయండి",
    hint: "గరిష్ఠంగా 10 MB · JPG, PNG, WebP",
    fit: "ఫిట్", fitContain: "పేజీలో సరిపోతుంది", fitCover: "పేజీని నింపండి",
    crop: "క్రాప్ & సర్దుబాటు",
    apply: "క్రాప్ వర్తింపజేయండి", printNow: "ఇప్పుడే ప్రింట్",
  },
  passport: {
    title: "పాస్‌పోర్ట్ ఫోటో ఎడిటర్",
    subtitle: "కట్ లైన్‌లతో A4 షీట్. సైజ్ ఎంచుకోండి, ఫోటో అప్‌లోడ్ చేయండి, ప్రింట్.",
    size: "ఫోటో పరిమాణం",
    copies: "షీట్‌కు కాపీలు",
    generate: "షీట్ ప్రింట్",
    tips: "సలహా: సాదా నేపథ్యంతో స్పష్టమైన ముఖ ఫోటో వాడండి.",
  },
  idCard: {
    title: "ID కార్డ్ డిజైనర్",
    subtitle: "ISO-సైజ్ (85.6 × 54 mm) ID కార్డ్‌లు సృష్టించండి. ఫీల్డ్‌లు నింపండి, ఫోటో అప్‌లోడ్ చేయండి, ప్రింట్.",
    template: "టెంప్లేట్",
    tpl_employee: "ఉద్యోగి", tpl_student: "విద్యార్థి", tpl_shop: "షాప్ పాస్",
    fields: { name: "పూర్తి పేరు", id_no: "ID నంబర్", role: "పాత్ర",
              org: "సంస్థ", phone: "ఫోన్" },
    generate: "ID కార్డ్ ప్రింట్",
  },
  reports: {
    title: "నివేదికలు", analytics: "అనలిటిక్స్",
    daily: "రోజువారీ", weekly: "వారానికి", monthly: "నెలవారీ",
    orders: "ఆర్డర్లు", pages: "పేజీలు", revenue: "ఆదాయం",
    colorBw: "రంగు / BW", trends: "ధోరణులు", ordersRevenue: "ఆర్డర్లు & ఆదాయం",
    noData: "ఈ కాలానికి ఇంకా డేటా లేదు.",
    daysCount: "రోజులు",
  },
  users: {
    title: "వినియోగదారులు",
    role: "పాత్ర", wallet: "వాలెట్", statusCol: "స్థితి",
    active: "క్రియాశీలం", inactive: "నిష్క్రియాత్మకం",
  },
  settings: {
    title: "ప్లాట్‌ఫారమ్ సెట్టింగ్‌లు",
    subtitle: "ధర, పన్నులు మరియు కమిషన్ రేట్లు.",
    priceBw: "పేజీకి B&W (₹)", priceColor: "పేజీకి రంగు (₹)",
    duplexExtra: "డ్యూప్లెక్స్ అదనపు పేజీకి (₹)", gstPct: "GST %",
    retailerPct: "రిటైలర్ కమిషన్ %", distributorPct: "డిస్ట్రిబ్యూటర్ కమిషన్ %",
    notifyEmail: "ఇమెయిల్ నోటిఫికేషన్‌లు పంపండి",
    saveChanges: "మార్పులు సేవ్ చేయండి",
  },
};

// Hindi
const hi = {
  brand: { tagline: "स्मार्ट क्लाउड प्रिंटिंग प्लेटफ़ॉर्म", name: "मा‌प्रिंट" },
  common: {
    signIn: "साइन इन", signOut: "साइन आउट", register: "पंजीकरण",
    email: "ईमेल", password: "पासवर्ड", name: "पूरा नाम", phone: "मोबाइल",
    save: "सहेजें", cancel: "रद्द", delete: "हटाएँ", edit: "संपादित",
    submit: "जमा करें", loading: "लोड हो रहा है…", search: "खोजें",
    yes: "हाँ", no: "नहीं", ok: "ठीक है", back: "वापस", close: "बंद",
    online: "ऑनलाइन", offline: "ऑफ़लाइन", pending: "लंबित",
    printing: "प्रिंट हो रहा है", completed: "पूर्ण", cancelled: "रद्द",
    failed: "विफल", queued: "पंक्ति में",
    all: "सभी", none: "कोई नहीं", copies: "प्रतियाँ", pages: "पृष्ठ",
    color: "रंगीन", bw: "श्वेत-श्याम", duplex: "डुप्लेक्स",
    paper: "कागज़", orientation: "अभिविन्यास", portrait: "लंबवत",
    landscape: "क्षैतिज", download: "डाउनलोड", upload: "अपलोड",
    filename: "फ़ाइल", status: "स्थिति", amount: "राशि", date: "तिथि",
    actions: "क्रियाएँ", type: "प्रकार", welcome: "वापसी पर स्वागत",
    language: "भाषा",
  },
  nav: {
    dashboard: "डैशबोर्ड", newPrint: "नया प्रिंट", bulkPrint: "बल्क प्रिंट",
    imagePrint: "इमेज प्रिंट", passport: "पासपोर्ट फोटो", idCard: "आईडी कार्ड",
    orders: "ऑर्डर", wallet: "वॉलेट", printers: "प्रिंटर",
    connector: "कनेक्टर", reports: "रिपोर्ट्स", users: "उपयोगकर्ता",
    settings: "सेटिंग्स", subscription: "सदस्यता", finance: "वित्त",
    myShop: "मेरी दुकान (QR)", printStudio: "प्रिंट स्टूडियो",
    help: "सहायता एवं सहयोग", about: "MaaPrint के बारे में",
  },
  auth: {
    signInTitle: "मा‌प्रिंट में साइन इन करें",
    orRegister: "या नया खाता बनाएँ",
    forgot: "भूल गए?",
    tryDemo: "डेमो खाते आज़माएँ",
    createTitle: "अपना खाता बनाएँ",
    haveAccount: "पहले से खाता है? साइन इन करें",
    iAmA: "मैं…",
    role_customer: "ग्राहक",
    role_retailer: "रिटेलर / CSC केंद्र",
    role_distributor: "वितरक",
    role_super_admin: "सुपर एडमिन",
    minPwd: "पासवर्ड (कम से कम 6 अक्षर)",
    resetTitle: "पासवर्ड रीसेट",
    resetSubtitle: "अपना ईमेल दर्ज करें, हम रीसेट लिंक भेजेंगे।",
    resetSent: "यदि ईमेल पंजीकृत है, तो रीसेट लिंक भेज दिया गया है।",
    sendLink: "रीसेट लिंक भेजें",
    backToSignIn: "साइन इन पर वापस",
  },
  dashboard: {
    overview: "अवलोकन",
    todaysPrints: "आज के प्रिंट",
    totalPages: "कुल पृष्ठ",
    revenue: "राजस्व",
    platformRevenue: "प्लेटफ़ॉर्म राजस्व",
    totalSpend: "कुल खर्च",
    walletBalance: "वॉलेट शेष",
    recharge: "रीचार्ज →",
    lastSevenDays: "पिछले 7 दिन",
    printActivity: "प्रिंट गतिविधि",
    trending: "ट्रेंडिंग",
    printerStatus: "प्रिंटर स्थिति",
    onlinePrinters: "ऑनलाइन प्रिंटर",
    readyToPrint: "प्रिंट के लिए तैयार",
    totalPrinters: "कुल प्रिंटर",
    allConfigured: "सभी कॉन्फ़िगर",
    managePrinters: "प्रिंटर प्रबंधित करें",
    recentActivity: "हाल की गतिविधि",
    recentOrders: "हाल के ऑर्डर",
    seeAll: "सभी देखें →",
    noPrints: "अभी तक कोई प्रिंट नहीं।",
    startPrinting: "प्रिंट शुरू करें →",
    newPrintBtn: "नया प्रिंट",
    allOrders: "सभी ऑर्डर",
    pendingJobs: "लंबित कार्य",
  },
  print: {
    pageTitle: "नया प्रिंट कार्य",
    subtitle: "PDF अपलोड करें, सेटिंग्स चुनें — मा‌प्रिंट चुपचाप प्रिंट करेगा।",
    dropzone: "PDF यहाँ छोड़ें या क्लिक करें",
    dropzoneHint: "अधिकतम 15 MB · केवल PDF",
    settings: "प्रिंट सेटिंग्स",
    summary: "ऑटो-प्रिंट सारांश",
    mode: "मोड", modeColor: "रंगीन", modeBW: "श्वेत-श्याम",
    duplexOn: "दोनों तरफ", duplexOff: "एक तरफ",
    yourWallet: "आपका वॉलेट",
    autoPrintNow: "ऑटो-प्रिंट करें",
    sending: "भेज रहे हैं…",
    silentNote: "मौन बैकग्राउंड प्रिंट · वॉलेट से स्वतः कटौती",
    ready: "प्रिंट पूर्ण!",
    readyNote: "मौन ऑटो-प्रिंट भेजा गया —",
    printAnother: "एक और प्रिंट",
    viewOrders: "सभी ऑर्डर देखें",
    charged: "शुल्क",
    perPageFrom: "प्रति पृष्ठ (से)",
    gst: "GST",
  },
  wallet: {
    title: "वॉलेट", currentBalance: "वर्तमान शेष",
    autoDeduct: "प्रत्येक प्रिंट पर स्वतः कटौती",
    addMoney: "पैसे जोड़ें", history: "लेन-देन इतिहास",
    empty: "अभी तक कोई लेन-देन नहीं।", credit: "क्रेडिट", debit: "डेबिट",
    recharge: "वॉलेट रीचार्ज", processing: "प्रोसेस हो रहा है…", now: "अभी रीचार्ज",
    razorpayHint: "कॉन्फ़िगर होने पर Razorpay असली भुगतान। वर्तमान में मॉक मोड।",
    entries: "प्रविष्टियाँ", description: "विवरण",
  },
  connector: {
    title: "विंडोज़ कनेक्टर",
    subtitle: "मौन ऑटो-प्रिंट के लिए अपने Windows PC पर MaaPrint एजेंट इंस्टॉल करें।",
    generate: "कनेक्टर की जनरेट करें",
    regenerate: "की पुनर्जनरेट",
    downloadPackage: "कनेक्टर डाउनलोड (.zip)",
    setupSteps: "सेटअप चरण",
    yourKey: "आपकी कनेक्टर की",
    keyWarn: "यह की केवल एक बार दिखाई जाती है — अभी सहेजें।",
    step1: "Windows PC पर Python 3.9+ और SumatraPDF इंस्टॉल करें।",
    step2: "नीचे दी गई ZIP डाउनलोड करें और अनज़िप करें।",
    step3: "config.example.json को config.json में कॉपी करें और की पेस्ट करें।",
    step4: "`python maaprint_connector.py --config config.json` चलाएँ।",
    step5: "वैकल्पिक: ऑटो-स्टार्ट के लिए Windows स्टार्टअप में जोड़ें।",
    connected: "कनेक्टेड एजेंट",
    lastSeen: "अंतिम बार देखा",
    noConnectors: "अभी तक कोई कनेक्टर पंजीकृत नहीं।",
  },
  printers: {
    title: "प्रिंटर",
    subtitle: "Windows कनेक्टर से स्वतः पहचाने जाते हैं। नीचे मैन्युअल प्रिंटर जोड़ें।",
    addPrinter: "प्रिंटर जोड़ें",
    name: "नाम", brand: "ब्रांड", model: "मॉडल",
    setDefault: "डिफ़ॉल्ट के रूप में सेट", default: "डिफ़ॉल्ट",
    testPrint: "टेस्ट", empty: "अभी कोई प्रिंटर कनेक्ट नहीं",
    emptyHint: "ऑटो-प्रिंट सक्षम करने के लिए पहला प्रिंटर जोड़ें।",
  },
  orders: {
    title: "ऑर्डर", history: "प्रिंट इतिहास",
    filenameCol: "फ़ाइल", user: "उपयोगकर्ता",
    pagesCopies: "पृष्ठ × प्रतियाँ",
    mode: "मोड", printer: "प्रिंटर", cost: "लागत",
    reprint: "पुनःप्रिंट", cancel: "रद्द", empty: "कोई ऑर्डर नहीं मिला।",
    filterAll: "सभी स्थिति", exportCsv: "CSV निर्यात",
  },
  bulk: {
    title: "बल्क PDF प्रिंट",
    subtitle: "एक साथ कई PDF — प्रत्येक अलग प्रिंट कार्य के रूप में क़तार में।",
    dropzone: "यहाँ कई PDF छोड़ें या ब्राउज़ करें",
    hint: "अधिकतम 20 फ़ाइलें, प्रत्येक 15 MB",
    printAll: "सभी प्रिंट",
    printAllQueue: "{count} प्रिंट कार्य भेजें",
    filesReady: "प्रिंट के लिए तैयार फ़ाइलें",
  },
  image: {
    title: "इमेज प्रिंट",
    subtitle: "JPG, PNG या WebP इमेज प्रिंट करें — पेपर साइज़ के अनुसार स्वतः स्केल।",
    dropzone: "इमेज यहाँ छोड़ें या ब्राउज़ करें",
    hint: "अधिकतम 10 MB · JPG, PNG, WebP",
    fit: "फिट", fitContain: "पृष्ठ में फ़िट", fitCover: "पूरा पृष्ठ भरें",
    crop: "क्रॉप & समायोजित",
    apply: "क्रॉप लागू करें", printNow: "अभी प्रिंट",
  },
  passport: {
    title: "पासपोर्ट फोटो एडिटर",
    subtitle: "कट लाइनों के साथ A4 शीट। साइज़ चुनें, फोटो अपलोड करें, प्रिंट।",
    size: "फोटो साइज़",
    copies: "प्रति शीट प्रतियाँ",
    generate: "शीट प्रिंट",
    tips: "सुझाव: सादे बैकग्राउंड के साथ स्पष्ट सामने से फोटो।",
  },
  idCard: {
    title: "ID कार्ड डिज़ाइनर",
    subtitle: "ISO-साइज़ (85.6 × 54 mm) ID कार्ड बनाएँ। फ़ील्ड भरें, फोटो अपलोड करें, प्रिंट।",
    template: "टेम्पलेट",
    tpl_employee: "कर्मचारी", tpl_student: "छात्र", tpl_shop: "शॉप पास",
    fields: { name: "पूरा नाम", id_no: "ID नंबर", role: "भूमिका",
              org: "संस्था", phone: "फ़ोन" },
    generate: "ID कार्ड प्रिंट",
  },
  reports: {
    title: "रिपोर्ट्स", analytics: "एनालिटिक्स",
    daily: "दैनिक", weekly: "साप्ताहिक", monthly: "मासिक",
    orders: "ऑर्डर", pages: "पृष्ठ", revenue: "राजस्व",
    colorBw: "रंगीन / BW", trends: "रुझान", ordersRevenue: "ऑर्डर & राजस्व",
    noData: "इस अवधि के लिए अभी कोई डेटा नहीं।",
    daysCount: "दिन",
  },
  users: {
    title: "उपयोगकर्ता",
    role: "भूमिका", wallet: "वॉलेट", statusCol: "स्थिति",
    active: "सक्रिय", inactive: "निष्क्रिय",
  },
  settings: {
    title: "प्लेटफ़ॉर्म सेटिंग्स",
    subtitle: "मूल्य, कर और कमीशन दरें.",
    priceBw: "प्रति पृष्ठ B&W (₹)", priceColor: "प्रति पृष्ठ रंगीन (₹)",
    duplexExtra: "डुप्लेक्स अतिरिक्त प्रति पृष्ठ (₹)", gstPct: "GST %",
    retailerPct: "रिटेलर कमीशन %", distributorPct: "वितरक कमीशन %",
    notifyEmail: "ईमेल नोटिफ़िकेशन भेजें",
    saveChanges: "परिवर्तन सहेजें",
  },
};

export const TRANSLATIONS = { en, te, hi };
